import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  amber: "#d97706", amberBg: "#fffbeb", amberBorder: "#fde68a", amberText: "#92400e",
  blue: "#2563eb", blueBg: "#eff6ff", blueBorder: "#bfdbfe",
  red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft:     <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    ArrowRight:    <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ChevronDown:   <><path d="m6 9 6 6 6-6"/></>,
    ChevronUp:     <><path d="m18 15-6-6-6 6"/></>,
    ChevronRight:  <><path d="m9 18 6-6-6-6"/></>,
    CheckCircle:   <><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></>,
    AlertTriangle: <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"/><path d="M12 9v4M12 17h.01"/></>,
    Star:          <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    Shield:        <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    ShieldCheck:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
    BadgeCheck:    <><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></>,
    Calendar:      <><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    Clock:         <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    CreditCard:    <><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></>,
    Wallet:        <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    MessageCircle: <><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></>,
    Users:         <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    User:          <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    Wrench:        <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></>,
    Home:          <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    Droplets:      <><path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/><path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/></>,
    Plug:          <><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8H6a2 2 0 0 0-2 2v2a7 7 0 1 0 14 0v-2a2 2 0 0 0-2-2Z"/></>,
    Paintbrush:    <><path d="M18.37 2.63 14 7l-1.59-1.59a2 2 0 0 0-2.82 0L8 7l9 9 1.59-1.59a2 2 0 0 0 0-2.82L17 10l4.37-4.37a2.12 2.12 0 1 0-3-3z"/><path d="M9 8c-2 2.5-2 5-2 7s2 4 2 6"/></>,
    Leaf:          <><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/></>,
    TrendingUp:    <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
    Zap:           <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    Info:          <><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></>,
    MapPin:        <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></>,
    Camera:        <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></>,
    Phone:         <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></>,
    Scissors:      <><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" x2="8.12" y1="4" y2="15.88"/><line x1="14.47" x2="20" y1="14.48" y2="20"/><line x1="8.12" x2="12" y1="8.12" y2="12"/></>,
    Truck:         <><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect width="7" height="7" x="14" y="10" rx="1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></>,
    Sun:           <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></>,
    Cloud:         <><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/></>,
    Umbrella:      <><path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7"/></>,
    Award:         <><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></>,
    Lock:          <><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const TABS = [
  { id: "clients",     label: "Hiring a Pro",      icon: "Users"  },
  { id: "pros",        label: "Working as a Pro",  icon: "Award"  },
  { id: "maintenance", label: "Home Maintenance",  icon: "Home"   },
];

const CLIENT_SECTIONS = [
  {
    id: "profile",
    icon: "BadgeCheck",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    label: "Reading a Pro's profile",
    title: "How to read a Pro's profile before you book",
    summary: "A JiraniPro profile is designed to tell you everything you need to make a confident decision. Here is what each element actually means and what to look for.",
    tips: [
      {
        heading: "The Verified badge",
        body: "The green Verified badge means JiraniApp has confirmed the Pro's national ID or passport and reviewed their background. This is the single most important trust signal on the platform. A Pro without this badge has not yet completed verification and cannot accept paid bookings.",
        callout: null,
      },
      {
        heading: "Star rating and number of reviews",
        body: "A high star average means little if it comes from only two or three reviews. Look for Pros with a consistent rating across at least 15–20 completed jobs. A Pro with 4.7 stars from 40 reviews is almost always a safer bet than one with 5.0 stars from 3. Read the written reviews, not just the number — look for specific mentions of punctuality, quality of work, and communication.",
        callout: null,
      },
      {
        heading: "Response rate and response time",
        body: "This tells you how reliably the Pro replies to enquiries and how quickly. A high response rate (above 90%) means they take communication seriously. If a Pro has a low response rate, they may be hard to reach once a job is booked.",
        callout: null,
      },
      {
        heading: "Portfolio photos",
        body: "Not all Pros upload portfolio photos, but those who do are showing you their real work. Look for variety — a plumber who only shows one type of fitting may have limited experience. A painter who shows different room types and finishes is demonstrating breadth. No photos at all is fine, but photos are always reassuring.",
        callout: null,
      },
      {
        heading: "Pricing display",
        body: "Some Pros list fixed prices, some list hourly rates, and some list 'quote on request' for jobs that vary by size. If a price seems unusually low, it may be a base rate that excludes materials or travel. Before confirming any booking, use the in-app message to clarify exactly what the quoted price includes.",
        callout: { type: "tip", text: "Always confirm whether the quoted price includes materials before booking. Many Pros charge separately for parts and supplies." },
      },
      {
        heading: "Service area",
        body: "Each Pro sets their own service area on their profile. If your location falls outside their listed coverage, you will not appear in their search results for your area. If you see a Pro you like who is slightly outside your area, you can message them directly — some Pros are willing to travel for the right job.",
        callout: null,
      },
    ],
  },
  {
    id: "booking",
    icon: "Calendar",
    color: T.blue,
    colorBg: T.blueBg,
    colorBorder: T.blueBorder,
    label: "Making a booking",
    title: "How to make a booking that goes smoothly",
    summary: "Most booking problems are avoidable. The steps below take two extra minutes and dramatically reduce the chance of a misunderstanding on the day.",
    tips: [
      {
        heading: "Write a detailed job description",
        body: "The more specific you are, the more accurately a Pro can price and plan. Instead of 'fix a leak,' write 'leaking pipe under the kitchen sink — water drips from the joint connecting the waste pipe to the drain.' Instead of 'paint a room,' write 'living room, approximately 5m x 4m, currently cream, want to change to white — ceiling is not included.' Specificity leads to better quotes and fewer surprises.",
        callout: null,
      },
      {
        heading: "Include accurate access instructions",
        body: "Many Pros waste significant time finding a client's property. Include your gate number, estate name, floor if in an apartment, and any access code or security procedure. If there is a guard at the gate, let them know in advance that someone is coming. Add your phone number in the notes so the Pro can call if they get lost.",
        callout: null,
      },
      {
        heading: "Check the Pro's availability before assuming",
        body: "The calendar on a Pro's profile shows their live availability. Only book slots where they are marked as free. Do not message a Pro asking them to take a job outside their listed hours — they have set those hours deliberately. If you need an urgent or out-of-hours booking, filter by Pros who have listed availability for that time.",
        callout: null,
      },
      {
        heading: "Ask your questions before confirming",
        body: "Use the in-app message thread to clarify anything that is not clear from the profile — whether materials are included, whether a second visit might be needed, or what happens if the scope of work grows once they arrive. A good Pro will answer clearly. If they do not respond or give vague answers, that tells you something important.",
        callout: { type: "warning", text: "Do not share your personal phone number or WhatsApp with a Pro before a booking is confirmed. Use in-app messaging — it protects your contact details and creates a record if anything needs to be disputed later." },
      },
      {
        heading: "Set a clear time window",
        body: "Whenever possible, book a specific time rather than 'anytime in the morning.' Pros manage multiple clients and a vague window makes scheduling difficult for both of you. If you are flexible, say so in the notes rather than leaving the time blank.",
        callout: null,
      },
    ],
  },
  {
    id: "safety",
    icon: "ShieldCheck",
    color: "#7c3aed",
    colorBg: "#fdf4ff",
    colorBorder: "#e9d5ff",
    label: "Safety before and during a job",
    title: "What to do before a Pro enters your home",
    summary: "Every JiraniPro is verified, but a few habits make every job safer and give you more confidence from the moment they arrive.",
    tips: [
      {
        heading: "Confirm the booking details in-app before the day",
        body: "The morning of the job, check your booking confirmation in the app. Make sure the name, date, time, and service match what you agreed. If anything looks different, message the Pro directly through the app before they arrive.",
        callout: null,
      },
      {
        heading: "Match the Pro to their profile photo when they arrive",
        body: "Every verified Pro has a profile photo linked to their verified ID. When someone arrives claiming to be your JiraniPro, briefly check the app. If the face does not match, do not let them in — contact JiraniApp Support immediately through the Help page.",
        callout: null,
      },
      {
        heading: "Do not pay outside the platform",
        body: "JiraniApp holds your payment securely and only releases it to the Pro after the job is done. A Pro asking you to pay cash upfront, or to transfer money to a personal number before starting, is acting outside the platform's terms. Decline and report this through the app. Cash payments are not covered by the Jirani Guarantee.",
        callout: { type: "warning", text: "If a Pro asks for upfront cash payment or tries to move the transaction off the platform, do not proceed. Report it through the Help page." },
      },
      {
        heading: "Secure valuables and personal documents before the Pro arrives",
        body: "This is standard practice for any home service. Store passports, cash, laptops, and jewellery in a drawer or cupboard before the Pro arrives. This protects you and avoids any ambiguity if something goes missing. It is not a reflection on the Pro specifically.",
        callout: null,
      },
      {
        heading: "Stay accessible during the job",
        body: "You do not need to watch over the Pro constantly, but be reachable by phone or in-app message. Practical questions often come up mid-job — a plumber may need to confirm which valve to close, or a painter may need to clarify a colour decision. Being available keeps the job moving and avoids delays.",
        callout: null,
      },
      {
        heading: "Do a walkthrough before signing off",
        body: "Before marking the job as complete in the app, walk through the work with the Pro. If anything looks incomplete or is not what you discussed, raise it now — while the Pro is still present. It is far easier to address an issue on the spot than through a dispute after the fact.",
        callout: { type: "tip", text: "Take a quick photo of the finished work before the Pro leaves. If you need to open a dispute within the 48-hour window, photos are the most useful evidence." },
      },
    ],
  },
  {
    id: "payment",
    icon: "CreditCard",
    color: T.amber,
    colorBg: T.amberBg,
    colorBorder: T.amberBorder,
    label: "Payments and disputes",
    title: "How payment works, and what to do if something goes wrong",
    summary: "Your money is always protected when you pay through JiraniApp. Here is exactly how the payment flow works and what steps to take if a job does not go as planned.",
    tips: [
      {
        heading: "How payment is held",
        body: "When you confirm a booking and pay via M-Pesa, your money is not sent to the Pro immediately. It is held securely by JiraniApp and only released once you mark the job as complete or the job status is automatically updated. This protects you in the event of a dispute.",
        callout: null,
      },
      {
        heading: "When to mark a job complete",
        body: "Mark the job as complete after your walkthrough confirms the work meets what was agreed. Do not mark it complete while the Pro is still working, or before you have checked everything. Once you mark a job complete, the payment release process begins and disputes become harder to resolve.",
        callout: null,
      },
      {
        heading: "What to do if the work is not right",
        body: "Do not mark the job complete if the work is not satisfactory. Message the Pro through the app and clearly describe what you are not happy with. Many issues are resolved this way — the Pro returns to fix the problem. If they refuse or do not respond, open a dispute through the Help page within 48 hours.",
        callout: null,
      },
      {
        heading: "How to open a dispute",
        body: "Go to Help and Support, find your booking, and select 'Open a dispute.' Describe what went wrong in detail and attach any photos or screenshots of messages. The Trust and Safety team will review both sides within 2 business days. You do not need to argue your case with the Pro — the team manages the process.",
        callout: { type: "tip", text: "Disputes must be raised within 48 hours of the scheduled or completed service. If you wait longer than this, the window closes and a refund becomes harder to process." },
      },
      {
        heading: "Cancellation charges",
        body: "Cancellations made at least 24 hours before the job time are free. If you cancel within 24 hours of the booking, a small cancellation fee may apply to compensate the Pro for the reserved time. The fee amount is shown at the moment you cancel, before you confirm. Rescheduling is usually free if done with enough notice.",
        callout: null,
      },
    ],
  },
  {
    id: "reviews",
    icon: "Star",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    label: "Leaving useful reviews",
    title: "How to leave a review that actually helps",
    summary: "A good review does more than give a star rating. It helps other clients make better decisions and gives great Pros the recognition they deserve.",
    tips: [
      {
        heading: "Be specific about what was good",
        body: "Saying 'great service' is less useful than 'arrived on time, explained the problem clearly, fixed the leak in under an hour and cleaned up the area before leaving.' Specific reviews help future clients know exactly what to expect, and help the Pro understand what they are doing well.",
        callout: null,
      },
      {
        heading: "Mention any issues honestly",
        body: "If something was not right — the Pro arrived 40 minutes late, or had to come back a second time — mention it. An honest 3-star review with context is more valuable to the community than a polite 5-star review that glosses over problems. Reviews that are only positive and vague are not very useful to anyone.",
        callout: null,
      },
      {
        heading: "Do not use reviews to threaten or negotiate",
        body: "If you have a complaint, raise it through the dispute process — not by threatening a bad review to get money back. This misuses the review system and is against JiraniApp's terms. The review system is for honest feedback, not leverage.",
        callout: null,
      },
      {
        heading: "Leave your review promptly",
        body: "The review window is open for 14 days after a completed job. Leaving it within a day or two while the job is fresh means your memory of the details is accurate. Waiting too long and your review may end up saying less than it should.",
        callout: null,
      },
    ],
  },
];

const PRO_SECTIONS = [
  {
    id: "profile-pro",
    icon: "User",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    label: "Building your profile",
    title: "How to build a profile that gets bookings",
    summary: "Your profile is your entire first impression on JiraniApp. Clients decide in seconds whether to look further. These tips make those seconds count.",
    tips: [
      {
        heading: "Write a bio that answers the client's actual question",
        body: "Clients reading your bio are asking: 'Can I trust this person in my home?' Your bio should answer that — not list your services again (they can see those). Mention how many years you have been working, the kinds of jobs you specialise in, and one or two things that make you reliable and easy to work with. Short and specific beats long and vague.",
        callout: null,
      },
      {
        heading: "Upload at least three portfolio photos",
        body: "Pros with portfolio photos get significantly more first bookings than those without. Take before-and-after photos of recent jobs. Good lighting matters — phone cameras are fine but try to take photos in daylight. Show a range of job types if you offer multiple services. Label each photo with what was done.",
        callout: { type: "tip", text: "Ask a satisfied client if you can photograph the finished work. Most are happy to say yes, and a real client's home looks more credible than a generic stock photo." },
      },
      {
        heading: "Set honest, competitive pricing",
        body: "Pricing too high means fewer bookings. Pricing too low undervalues your work and attracts clients who may not treat you well. Check what other Pros in your category and area are charging — the platform gives you a market range. Starting slightly competitive when building your reviews is a legitimate strategy; you can raise rates once your reputation is established.",
        callout: null,
      },
      {
        heading: "Complete verification as early as possible",
        body: "Unverified Pros cannot accept paid bookings. The verification process typically takes 24–48 hours on business days. Submit your ID as soon as you create your profile so you are not waiting once you are ready to start taking work. Your verified badge is the single biggest trust signal to new clients.",
        callout: null,
      },
      {
        heading: "List every service you are genuinely confident in",
        body: "Only list services you are actually qualified and experienced to perform. Clients filter by service type, and your profile will appear in those search results. If you list something you cannot do well and a client books it, a bad review can damage all your other listings. Accuracy protects your reputation.",
        callout: null,
      },
    ],
  },
  {
    id: "bookings-pro",
    icon: "Calendar",
    color: T.blue,
    colorBg: T.blueBg,
    colorBorder: T.blueBorder,
    label: "Managing bookings",
    title: "How to manage your bookings like a top-rated Pro",
    summary: "Consistency and communication are what separate Pros with 50 bookings from Pros with 500. These habits are the difference.",
    tips: [
      {
        heading: "Respond to enquiries within a few hours",
        body: "Your response rate is visible to clients on your profile. A high response rate builds trust before a single job is done. If you are busy, send a brief reply acknowledging the enquiry and letting the client know when you can give a fuller answer. Clients who do not hear back within a few hours often move on to the next Pro.",
        callout: null,
      },
      {
        heading: "Keep your availability up to date",
        body: "Inaccurate availability frustrates clients. If your listed hours say you are free on Saturday morning but you are actually busy, a client may book and then you have to cancel — which damages your profile. Update your calendar at the end of each week to reflect the coming week. Set a reminder if you need one.",
        callout: null,
      },
      {
        heading: "Confirm every booking with a brief message",
        body: "Even if the booking is confirmed in-app, sending a short message the day before — 'Confirmed for tomorrow at 10am, see you then' — dramatically reduces cancellations and no-contact situations. Clients who hear from you before the day feel more confident and are more likely to be home and ready.",
        callout: null,
      },
      {
        heading: "What to do if you need to cancel",
        body: "Cancel as early as possible — at least 24 hours before is the minimum. Use the app to cancel, not a text message. Write a brief explanation in the cancellation message. Frequent cancellations reduce your visibility in search results. If you are going through a period of unavailability, set yourself to 'Unavailable' temporarily rather than accepting and cancelling jobs.",
        callout: { type: "warning", text: "Cancelling after a client has made travel or access arrangements is taken seriously by the Trust and Safety team. Where possible, cancel with at least 24 hours' notice." },
      },
      {
        heading: "Update the job status as you go",
        body: "Mark yourself as 'on my way' when leaving, update status milestones through the job, and mark it complete promptly once you are done. Clients can see these updates in real time. It shows professionalism, reduces client anxiety, and creates a clear record if any dispute arises later.",
        callout: null,
      },
    ],
  },
  {
    id: "earnings",
    icon: "TrendingUp",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    label: "Growing your earnings",
    title: "How to grow your income on JiraniApp over time",
    summary: "The platform rewards consistency and quality. These are the habits of Pros who go from occasional bookings to a full, predictable income.",
    tips: [
      {
        heading: "Reviews compound — get your first ten quickly",
        body: "Your first ten reviews do the most work. With zero reviews, clients have no social proof and may skip your profile regardless of your pricing. Focus your first month on delivering excellent work, communicating clearly, and politely encouraging clients to leave a review after the job. Once you have ten solid reviews, bookings begin to arrive more independently.",
        callout: null,
      },
      {
        heading: "Rebookings are your most valuable clients",
        body: "A client who books you twice is worth more than two first-time clients. After a job, let the client know they can rebook you directly through the platform whenever they need the same service again. Offer to note anything specific about their home — access codes, preferred timing, recurring requirements — so future bookings are frictionless for them.",
        callout: null,
      },
      {
        heading: "Do not undercut yourself to compete on price alone",
        body: "Clients who book on price alone are often the hardest to work with and least likely to leave good reviews. Compete on quality, communication, and reliability — then price fairly for that value. Gradually raising your rates as your reviews grow is a sign the platform is working for you, not an exception.",
        callout: null,
      },
      {
        heading: "Expand your service categories carefully",
        body: "Once you have strong reviews in one category, adding a closely related category can open new clients without starting from zero on reputation. A plumber who also handles basic waterproofing, or a painter who also does minor carpentry, naturally extends their reach. Add categories only where you have real competence — a bad review in a new category affects your whole profile.",
        callout: null,
      },
      {
        heading: "Track your best booking days and times",
        body: "Your earnings dashboard shows when you get the most bookings. Most Pros find certain days or time windows are consistently more active. Ensure your availability is fully open during those peak periods. Even one extra booking per week at your average rate adds up to meaningful income over a year.",
        callout: { type: "tip", text: "In Nairobi, Friday mornings and Saturday mornings are typically the highest-demand booking windows for most service categories. Make sure you are fully available if your schedule allows." },
      },
    ],
  },
  {
    id: "disputes-pro",
    icon: "ShieldCheck",
    color: "#7c3aed",
    colorBg: "#fdf4ff",
    colorBorder: "#e9d5ff",
    label: "Handling disputes",
    title: "How to handle client complaints and disputes professionally",
    summary: "A dispute does not have to damage your reputation if you handle it the right way. Most issues can be resolved before they escalate.",
    tips: [
      {
        heading: "Address problems during the job, not after",
        body: "If you run into something unexpected — a hidden structural issue, materials that are insufficient, a scope that is bigger than quoted — tell the client immediately and clearly. Explain what you found, what it means, and what the options are. Clients who feel informed and respected almost never escalate. Surprises discovered after the fact almost always do.",
        callout: null,
      },
      {
        heading: "Document your work throughout",
        body: "Take photos before you start, during the job for complex or invasive work, and after completion. If a client claims damage or incomplete work, your photos provide the clearest possible counter-evidence. Store these photos for at least 30 days after each job.",
        callout: null,
      },
      {
        heading: "Respond to dispute notifications immediately",
        body: "If a client opens a dispute, you will receive a notification through the app. Respond within 24 hours with your account of events, including any photos, messages, or records. The Trust and Safety team reviews disputes fairly — giving your side promptly and clearly is the most important thing you can do.",
        callout: null,
      },
      {
        heading: "Do not argue with the client directly",
        body: "If a client is unhappy, do not engage in a heated back-and-forth over messaging. Acknowledge their concern, tell them you take it seriously, and if the issue cannot be resolved between you immediately, let them know the platform team will review it. Keeping your messages professional protects you — everything in-app is visible to the Trust and Safety team.",
        callout: { type: "tip", text: "A polite, professional response to a dispute — even one you believe is unfounded — makes a far stronger impression on the reviewing team than a defensive or emotional reply." },
      },
    ],
  },
];

const MAINTENANCE_SECTIONS = [
  {
    id: "rainy",
    icon: "Umbrella",
    color: T.blue,
    colorBg: T.blueBg,
    colorBorder: T.blueBorder,
    label: "Before the long rains",
    title: "What to check before Kenya's long rains (March–May)",
    summary: "Most rain-related home damage is preventable. These checks, done in February or early March, protect your property through the season.",
    tips: [
      {
        heading: "Inspect and clear all gutters and downpipes",
        body: "Blocked gutters are the leading cause of water damage to walls, fascias, and foundations during heavy rains. Check that gutters are securely attached, have no cracks, and are clear of debris — especially dead leaves and bird nests. Downpipes should direct water at least 1 metre from the building's foundation. Book a handyman or roofer to clear gutters before the rains start.",
        callout: null,
      },
      {
        heading: "Check your roof for loose tiles or sheets",
        body: "Walk the perimeter of your home and look up. Loose, cracked, or shifted roof tiles or sheets are easy to spot from ground level in many cases. On flat roofs, look for areas where the waterproof membrane is bubbling or torn. Small roof problems become large ones the moment sustained rain arrives. Book a roofer to inspect and fix anything that looks compromised.",
        callout: { type: "warning", text: "Do not attempt to walk on a tiled or corrugated iron roof yourself. A slip from roof height causes serious injury. Book a qualified roofer for any inspection that requires going up." },
      },
      {
        heading: "Test your drainage and ensure water has somewhere to go",
        body: "Pour a bucket of water near your building's base and watch where it flows. It should move away from the foundation, not pool against the wall. If water sits near the building, you may need drainage channels cut or ground re-graded slightly. This is a relatively low-cost intervention that prevents expensive structural damp over time.",
        callout: null,
      },
      {
        heading: "Check exterior walls for existing cracks",
        body: "Hairline cracks are often cosmetic, but cracks wider than 3mm — especially horizontal cracks, stair-step cracks in brickwork, or cracks that go through the full wall — can allow water ingress. Book a painter or plastering specialist to assess and seal exterior cracks before the rains. Water inside a wall degrades the structure over seasons.",
        callout: null,
      },
      {
        heading: "Service external doors and window seals",
        body: "Check that all external doors and windows close and seal properly. Swollen frames, broken seals, or gaps around window edges allow rain to enter. Replacing a window seal costs very little compared to water-damaged flooring or furniture. A handyman or carpenter can reseal or re-seal frames in a few hours.",
        callout: null,
      },
    ],
  },
  {
    id: "dry",
    icon: "Sun",
    color: T.amber,
    colorBg: T.amberBg,
    colorBorder: T.amberBorder,
    label: "Dry season maintenance",
    title: "What to do during Kenya's dry seasons (Jan–Feb and July–Oct)",
    summary: "Dry conditions are the best time for exterior work, painting, and structural repairs. Here is how to use the dry months well.",
    tips: [
      {
        heading: "Repaint exterior surfaces",
        body: "Exterior paint requires dry weather to bond correctly and cure properly. The dry months are the ideal window to repaint exterior walls, gates, fences, and window frames. Painting in humid or wet conditions leads to peeling within months. Book a painter in late January or early July for the best results — demand is lower and scheduling is easier.",
        callout: null,
      },
      {
        heading: "Repair or resurface driveways and paving",
        body: "Cracked paving and potholed driveways deteriorate faster once the rains expose the substrate beneath. Repair paving cracks and resurface any deteriorated sections during the dry season. Concrete needs dry weather to cure and set correctly — rain on fresh concrete within 24 hours can significantly weaken it.",
        callout: null,
      },
      {
        heading: "Service water tanks and check capacity",
        body: "In areas with unreliable supply during dry months, check your tank's capacity and condition. Clean your water tank — most should be cleaned every 6–12 months. Sediment builds up over time and affects water quality. A tank cleaning professional can empty, scrub, and disinfect a standard rooftop tank in 2–3 hours.",
        callout: { type: "tip", text: "Coastal homes in Mombasa and Malindi typically need water tanks cleaned more frequently than Nairobi due to humidity and algae growth. Every 4–6 months is a reasonable schedule for coastal properties." },
      },
      {
        heading: "Clear garden overgrowth before the rains return",
        body: "Overgrown gardens become much harder and more expensive to manage once the rains start and growth accelerates. Trim trees away from the building (branches over the roof are a risk in heavy rain and wind), cut back hedges, and remove any dead wood. Book a gardener in August or September before the short rains begin.",
        callout: null,
      },
      {
        heading: "Check electrical connections in outbuildings and gates",
        body: "Exterior electrical connections — gate motors, security lights, outbuilding sockets — are exposed to weather year-round. During the dry season, have a qualified electrician check these connections, replace corroded terminals, and ensure all outdoor sockets are properly weatherproofed. Water getting into an outdoor socket or gate motor can cause fires and expensive motor failure.",
        callout: null,
      },
    ],
  },
  {
    id: "water",
    icon: "Droplets",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    label: "Water systems",
    title: "How to maintain your home's water systems",
    summary: "Water-related problems are among the most common — and most damaging — issues in Kenyan homes. Most are avoidable with basic maintenance.",
    tips: [
      {
        heading: "Locate and know how to use your main water shutoff",
        body: "Every member of your household should know where the main water shutoff valve is and how to close it. In an emergency — a burst pipe, a flooding appliance — getting to that valve in seconds prevents thousands of shillings in water damage. If you do not know where yours is, a plumber can locate it and label it for you in under 30 minutes.",
        callout: null,
      },
      {
        heading: "Act on slow drains immediately",
        body: "A slow drain is a partial blockage. Left alone, it becomes a full blockage — and full blockages back up into the house, which is a much larger problem. A slow shower drain, a gurgling kitchen sink, or a toilet that drains slowly are all early warnings. Address them before they escalate. A plumber can clear most residential blockages in under an hour.",
        callout: null,
      },
      {
        heading: "Check under sinks regularly",
        body: "The underside of kitchen and bathroom sinks hides slow leaks that can rot cabinets, damage flooring, and cause mould before they are ever noticed on the surface. Every few months, open the cabinet under each sink and feel the pipes and joints. Dampness, discolouration of the cabinet floor, or an earthy smell all indicate a leak. Catching it early costs far less than replacing a cabinet.",
        callout: null,
      },
      {
        heading: "Do not ignore damp patches on walls",
        body: "A damp patch on an interior wall that is not near a door or window is almost always a slow pipe leak inside the wall. The longer it is ignored, the more structural damage accumulates. A plumber can locate and fix the source, and a plasterer can restore the wall. The combined cost is usually modest if caught early — but grows quickly if the wall is repeatedly damp over months.",
        callout: null,
      },
      {
        heading: "Service your hot water boiler or geyser annually",
        body: "Electric geysers and gas water heaters should be inspected and serviced every 12 months. A faulty pressure relief valve — a common failure — can be dangerous. Sediment buildup in electric geysers reduces efficiency and shortens the unit's life. Annual servicing by a qualified plumber or appliance technician is cheaper than an emergency replacement.",
        callout: { type: "warning", text: "Never attempt to service a gas water heater yourself. Gas connection and servicing must be done by a qualified technician. Incorrect gas connections are a fire and explosion hazard." },
      },
    ],
  },
  {
    id: "electrical",
    icon: "Plug",
    color: T.amber,
    colorBg: T.amberBg,
    colorBorder: T.amberBorder,
    label: "Electrical safety",
    title: "Electrical safety and maintenance for your home",
    summary: "Electrical faults are one of the leading causes of residential fires in Kenya. These checks take almost no time and significantly reduce your risk.",
    tips: [
      {
        heading: "Take tripping circuit breakers seriously",
        body: "A circuit breaker that trips once and never trips again likely just had a power surge. A circuit breaker that trips repeatedly is telling you something is wrong — an overloaded circuit, a faulty appliance, or a wiring fault. Do not just keep resetting it. Have a qualified electrician diagnose the cause. Repeated tripping that is ignored can lead to overheating and fire.",
        callout: null,
      },
      {
        heading: "Look for scorch marks and burning smells near sockets",
        body: "Scorch marks around a socket, a burning plastic smell, or a socket that feels warm to the touch are serious warning signs. Stop using that socket immediately and have it replaced by a licensed electrician. Do not assume it is cosmetic — heat from a faulty connection is how fires start inside walls.",
        callout: { type: "warning", text: "A warm socket or switch plate is an emergency. Stop using it immediately, switch off the circuit at the consumer unit if possible, and book an electrician that day." },
      },
      {
        heading: "Do not overload extension leads",
        body: "Plugging multiple high-draw appliances — a kettle, a microwave, a toaster — into the same extension lead is a common cause of house fires. Each extension lead has a maximum rated load, usually printed on the packaging. High-wattage appliances should be plugged directly into wall sockets wherever possible, not chained through extension leads.",
        callout: null,
      },
      {
        heading: "Ensure outdoor lights and sockets are weatherproof rated",
        body: "Any socket or light fitting used outdoors must be rated for outdoor use (look for IP44 or higher on the specification). Standard indoor fittings installed outside corrode quickly and allow water ingress, which creates short circuits. If you are installing or replacing outdoor electrical fittings, use weatherproof rated products and have a licensed electrician do the installation.",
        callout: null,
      },
      {
        heading: "Have your home's wiring checked if it is more than 20 years old",
        body: "Older Kenyan homes often have wiring that was adequate at the time but does not meet modern load demands — particularly now that more appliances are used simultaneously. Insulation on older wiring degrades over time. If your home has not had an electrical inspection in the past decade, booking a qualified electrician for a full check is worthwhile.",
        callout: null,
      },
    ],
  },
  {
    id: "garden",
    icon: "Leaf",
    color: T.g500,
    colorBg: T.g50,
    colorBorder: T.g100,
    label: "Garden and outdoor spaces",
    title: "Keeping your outdoor spaces in good condition",
    summary: "Garden and outdoor maintenance is often deferred until problems become expensive. These habits keep costs low and your property looking its best.",
    tips: [
      {
        heading: "Trim trees away from the building",
        body: "Branches overhanging the roof are a liability in high winds and during heavy rain. They deposit leaf debris in gutters, can scrape and damage roof surfaces, and in storms can fall directly onto the structure. Branches should be kept at least 2 metres clear of the building's walls and roof. A gardener or tree trimmer can assess and cut back to a safe clearance.",
        callout: null,
      },
      {
        heading: "Keep drains and gulley pots clear of leaf debris",
        body: "Outdoor drains — particularly those near trees — block quickly with leaf fall. A blocked outdoor drain means rain water pools near the building rather than flowing away. Make clearing these drains part of your monthly routine during seasons when trees are dropping leaves. It takes five minutes and prevents far larger drainage problems.",
        callout: null,
      },
      {
        heading: "Check perimeter walls and fences for instability",
        body: "Boundary walls and fences need periodic inspection. Look for cracks at the base, mortar that is falling out between blocks, leaning sections, or posts that have shifted. A wall that is structurally compromised can fall — onto a vehicle, a person, or into a neighbour's property. This is both a safety issue and a liability issue. A mason can assess and reinforce weak sections before failure.",
        callout: null,
      },
      {
        heading: "Repaint metal gates and fences before rusting through",
        body: "In Kenya's climate, bare metal rusts fast. Surface rust is cosmetic; through-rust is structural. If your gate or fence shows rust, sand the affected area and apply a rust-inhibiting primer before repainting. Doing this annually is far cheaper than replacing a gate entirely. A painter can treat and repaint a standard gate in half a day.",
        callout: null,
      },
    ],
  },
];

/* ─────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────── */

function Callout({ type, text }) {
  const isWarning = type === "warning";
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "flex-start",
      padding: "12px 14px", borderRadius: 10, marginTop: 14,
      background: isWarning ? T.amberBg : T.g50,
      border: `1px solid ${isWarning ? T.amberBorder : T.g100}`,
    }}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        <Icon name={isWarning ? "AlertTriangle" : "Info"} size={15}
          color={isWarning ? T.amber : T.g600} strokeWidth={2} />
      </span>
      <p style={{
        margin: 0, fontSize: 13,
        color: isWarning ? T.amberText : T.g700,
        lineHeight: 1.65, fontFamily: T.sans,
      }}>{text}</p>
    </div>
  );
}

function TipAccordion({ tip, index, accentColor }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.n100}` }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "flex-start",
        justifyContent: "space-between", gap: 16,
        padding: "18px 0", background: "none", border: "none",
        cursor: "pointer", textAlign: "left", fontFamily: T.sans,
      }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flex: 1 }}>
          <span style={{
            width: 26, height: 26, borderRadius: 8, flexShrink: 0,
            background: open ? accentColor : T.n50,
            border: `1px solid ${open ? accentColor : T.n150}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11, fontWeight: 700,
            color: open ? T.white : T.n400,
            fontFamily: T.sans, transition: "all .18s", marginTop: 2,
          }}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span style={{
            fontSize: 15, fontWeight: 600, color: open ? T.n900 : T.n800,
            lineHeight: 1.4, letterSpacing: "-0.01em",
          }}>{tip.heading}</span>
        </div>
        <span style={{ flexShrink: 0, marginTop: 3, color: T.n400 }}>
          <Icon name={open ? "ChevronUp" : "ChevronDown"} size={17} strokeWidth={2} />
        </span>
      </button>
      <div style={{
        overflow: "hidden",
        maxHeight: open ? 600 : 0,
        transition: "max-height .32s cubic-bezier(.4,0,.2,1)",
      }}>
        <div style={{ paddingLeft: 40, paddingBottom: 20 }}>
          <p style={{ fontSize: 14, color: T.n600, lineHeight: 1.8, margin: 0 }}>{tip.body}</p>
          {tip.callout && <Callout type={tip.callout.type} text={tip.callout.text} />}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ section, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{
      borderRadius: 16,
      border: `1.5px solid ${open ? section.colorBorder : T.n150}`,
      background: T.white,
      overflow: "hidden",
      transition: "border-color .2s",
    }}>
      {/* Section header */}
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", background: open ? section.colorBg : T.white,
        border: "none", cursor: "pointer", padding: "22px 24px",
        display: "flex", alignItems: "flex-start", gap: 16,
        textAlign: "left", fontFamily: T.sans,
        transition: "background .2s",
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 13, flexShrink: 0,
          background: open ? T.white : T.n50,
          border: `1.5px solid ${open ? section.colorBorder : T.n150}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .2s",
        }}>
          <Icon name={section.icon} size={20} color={open ? section.color : T.n400} strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase", marginBottom: 4,
            color: open ? section.color : T.n400, transition: "color .2s",
          }}>{section.label}</div>
          <h3 style={{
            fontFamily: T.serif, fontSize: "clamp(18px,3vw,22px)", fontWeight: 400,
            color: T.n900, margin: "0 0 6px", letterSpacing: "-0.02em", lineHeight: 1.25,
          }}>{section.title}</h3>
          <p style={{ fontSize: 13, color: T.n500, margin: 0, lineHeight: 1.6 }}>{section.summary}</p>
        </div>
        <span style={{
          flexShrink: 0, marginTop: 10,
          width: 28, height: 28, borderRadius: "50%",
          background: open ? section.color : T.n100,
          border: `1px solid ${open ? section.color : T.n200}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all .2s",
        }}>
          <Icon name={open ? "ChevronUp" : "ChevronDown"} size={14}
            color={open ? T.white : T.n400} strokeWidth={2.5} />
        </span>
      </button>

      {/* Tips */}
      <div style={{
        maxHeight: open ? 3000 : 0, overflow: "hidden",
        transition: "max-height .4s cubic-bezier(.4,0,.2,1)",
      }}>
        <div style={{ padding: "0 24px 8px" }}>
          {section.tips.map((tip, i) => (
            <TipAccordion key={i} tip={tip} index={i} accentColor={section.color} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function TipsAndResourcesPage() {
  useFonts();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("clients");

  const sections = activeTab === "clients"
    ? CLIENT_SECTIONS
    : activeTab === "pros"
    ? PRO_SECTIONS
    : MAINTENANCE_SECTIONS;

  const tabMeta = {
    clients: {
      eyebrow: "For Clients",
      headline: <>How to get the most out of <em style={{ fontStyle: "italic", color: T.g600 }}>every booking</em></>,
      sub: "Practical guides on choosing the right Pro, booking confidently, staying safe, handling payments, and leaving reviews that count.",
    },
    pros: {
      eyebrow: "For JiraniPros",
      headline: <>Build a business that <em style={{ fontStyle: "italic", color: T.g600 }}>grows with you</em></>,
      sub: "Advice on your profile, managing bookings, growing your income, and handling the occasional dispute with professionalism.",
    },
    maintenance: {
      eyebrow: "Home Maintenance",
      headline: <>Protect your home, <em style={{ fontStyle: "italic", color: T.g600 }}>season by season</em></>,
      sub: "Kenya-specific maintenance guides covering rainy season prep, dry season work, water systems, electrical safety, and your garden.",
    },
  };

  const meta = tabMeta[activeTab];

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900, paddingBottom: 100 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Nav ── */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${T.n100}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate(-1)} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
        <button onClick={() => navigate("/signup")} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: T.g500, border: "none", cursor: "pointer",
          color: T.white, fontSize: 13, fontFamily: T.sans,
          padding: "8px 16px", borderRadius: 8, fontWeight: 600,
        }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}>
          Get started free
          <Icon name="ArrowRight" size={13} strokeWidth={2.2} color={T.white} />
        </button>
      </div>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(52px,7vw,88px) 20px clamp(44px,6vw,64px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", marginBottom: 22 }}>
            <Icon name="Zap" size={13} color={T.g500} strokeWidth={2.2} />
            Tips and Resources
          </div>
          <h1 style={{
            fontFamily: T.serif, fontSize: "clamp(38px,7vw,66px)", fontWeight: 400,
            margin: "0 0 18px", lineHeight: 1.06, color: T.n900, letterSpacing: "-0.03em",
            maxWidth: 640,
          }}>
            Everything you need to<br/>
            <em style={{ fontStyle: "italic", color: T.g600 }}>use JiraniApp well</em>
          </h1>
          <p style={{ fontSize: "clamp(15px,2vw,17px)", color: T.n600, lineHeight: 1.75, margin: "0 0 36px", maxWidth: 520 }}>
            Whether you are booking your first service, growing a professional practice,
            or keeping a home in good condition through the seasons — this is your reference.
          </p>

          {/* Tab selector */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TABS.map(tab => {
              const active = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  padding: "10px 18px", borderRadius: 99, cursor: "pointer",
                  fontFamily: T.sans, fontSize: 13, fontWeight: active ? 600 : 400,
                  border: `1.5px solid ${active ? T.g500 : T.n200}`,
                  background: active ? T.g500 : T.white,
                  color: active ? T.white : T.n600,
                  transition: "all .18s",
                }}>
                  <Icon name={tab.icon} size={14} color={active ? T.white : T.n400} strokeWidth={2} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══ TAB CONTENT HEADER ══ */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 20px" }}>
        <div style={{ padding: "clamp(40px,6vw,60px) 0 28px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 10px" }}>
            {meta.eyebrow}
          </p>
          <h2 style={{
            fontFamily: T.serif, fontSize: "clamp(26px,4.5vw,38px)", fontWeight: 400,
            color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.15,
          }}>
            {meta.headline}
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: 0, lineHeight: 1.7, maxWidth: 560 }}>
            {meta.sub}
          </p>
        </div>

        {/* Quick jump pills */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", paddingBottom: 36, borderBottom: `1px solid ${T.n100}`, marginBottom: 32 }}>
          {sections.map((s, i) => (
            <button key={s.id} onClick={() => {
              document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "7px 13px", borderRadius: 99, cursor: "pointer",
              fontFamily: T.sans, fontSize: 12, fontWeight: 500,
              border: `1px solid ${T.n150}`, background: T.n50,
              color: T.n600, transition: "all .15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.g200; e.currentTarget.style.color = T.g700; e.currentTarget.style.background = T.g50; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.n150; e.currentTarget.style.color = T.n600; e.currentTarget.style.background = T.n50; }}>
              <Icon name={s.icon} size={12} strokeWidth={2} />
              {s.label}
            </button>
          ))}
        </div>

        {/* ══ SECTION CARDS ══ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: 40 }}>
          {sections.map((section, i) => (
            <div key={section.id} id={`section-${section.id}`}>
              <SectionCard section={section} defaultOpen={i === 0} />
            </div>
          ))}
        </div>

        {/* ══ FOOTER CTA ══ */}
        <div style={{
          padding: "clamp(36px,5vw,52px)", borderRadius: 20,
          border: `1px solid ${T.n150}`, textAlign: "center",
          background: T.n50, marginBottom: 20,
        }}>
          <div style={{
            width: 50, height: 50, borderRadius: 14, background: T.g50,
            border: `1.5px solid ${T.g200}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 18px",
          }}>
            <Icon name="ShieldCheck" size={22} color={T.g500} strokeWidth={1.6} />
          </div>
          <h2 style={{
            fontFamily: T.serif, fontSize: "clamp(26px,4vw,36px)", fontWeight: 400,
            color: T.n900, margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.2,
          }}>
            Still have questions?
          </h2>
          <p style={{ fontSize: 15, color: T.n400, margin: "0 auto 28px", lineHeight: 1.75, maxWidth: 400 }}>
            Our Help and Support team can answer anything not covered here.
            Most questions are resolved within a few hours.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/help")} style={{
              padding: "13px 28px", background: T.g500, color: T.white,
              border: "none", borderRadius: 11, fontSize: 14, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
              onMouseEnter={e => e.currentTarget.style.background = T.g600}
              onMouseLeave={e => e.currentTarget.style.background = T.g500}>
              Visit Help and Support
              <Icon name="ArrowRight" size={15} color={T.white} strokeWidth={2} />
            </button>
            <button onClick={() => navigate("/community")} style={{
              padding: "13px 24px", background: "transparent", color: T.n700,
              border: `1.5px solid ${T.n200}`, borderRadius: 11, fontSize: 14, fontWeight: 600,
              fontFamily: T.sans, cursor: "pointer",
            }}
              onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
              Browse Community Hub
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}